class ValueConverter {
  static qualityToServer(quality) {
    return Math.round(quality * 100)
  }

  static qualityFromServer(quality) {
    return quality / 100
  }
}

export default class ImageProcessor {
  constructor(options = {}) {
    this.config = {
      maxFileSize: options.maxFileSize || 5 * 1024 * 1024,
      maxWidth: options.maxWidth || 1200,
      maxHeight: options.maxHeight || 1200,
      defaultQuality: options.defaultQuality || 0.7,
      compressedQuality: options.compressedQuality || 0.5,
      nativeTypes: ['image/jpeg', 'image/png', 'image/gif'],
      convertibleTypes: ['image/tiff', 'image/tif'],
      // Add Rails endpoint configuration
      tiffEndpoint: options.tiffEndpoint || '/image_details/convert_tiff',
      csrfToken: document.querySelector('[name="csrf-token"]')?.content,
    }
  }

  /**
   * Check if file type is supported
   * @param {string} fileType - MIME type of the file
   * @returns {{ supported: boolean, needsConversion: boolean }}
   */
  checkFileSupport = (fileType) => {
    return {
      supported: [...this.config.nativeTypes, ...this.config.convertibleTypes].includes(fileType),
      needsConversion: this.config.convertibleTypes.includes(fileType),
    }
  }

  /**
   * Convert TIFF file using Rails backend
   * @param {File} file - The TIFF file to convert
   * @returns {Promise<{ dataUrl: string, originalWidth: number, originalHeight: number }>}
   */
  async convertTiffWithRails(file) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('max_width', this.config.maxWidth)
    formData.append('max_height', this.config.maxHeight)
    formData.append('quality', ValueConverter.qualityToServer(this.config.defaultQuality))

    try {
      const response = await fetch(this.config.tiffEndpoint, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.config.csrfToken,
        },
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`)
      }

      return await response.json()
    }
    catch (error) {
      throw new Error(`TIFF conversion failed: ${error.message}`)
    }
  }

  /**
   * Check if file type is supported
   * @param {string} fileType - MIME type of the file
   * @returns {boolean}
   */
  isPreviewable = (fileType) => {
    return this.config.previewableTypes.includes(fileType)
  }

  /**
   * Determine output format for the image
   * @param {string} inputFormat - Input image MIME type
   * @param {number} fileSize - Original file size
   * @returns {string} Output format to use
   */
  determineOutputFormat = (inputFormat, fileSize) => {
    // If file is too large, convert to JPEG for better compression
    if (fileSize > this.config.maxFileSize) {
      return 'image/jpeg'
    }

    // Check if we have a specific output format for this input
    const targetFormat = this.config.nativeTypes[inputFormat]
    return targetFormat || inputFormat // Fall back to input format if no conversion needed
  }

  /**
   * Convert a File/Blob to a data URL
   * @param {File|Blob} file - The file to process
   * @returns {Promise<string>} - Returns a promise that resolves with the data URL
   */
  fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = e => resolve(e.target.result)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  /**
   * Create an Image object from a data URL
   * @param {string} dataUrl - The data URL to convert
   * @returns {Promise<HTMLImageElement>}
   */
  dataUrlToImage = (dataUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = dataUrl
    })
  }

  /**
   * Calculate new dimensions maintaining aspect ratio
   * @param {number} width - Original width
   * @param {number} height - Original height
   * @returns {{ width: number, height: number }}
   */
  calculateDimensions = (width, height) => {
    const { maxWidth, maxHeight } = this.config

    if (width <= maxWidth && height <= maxHeight) {
      return { width, height }
    }

    if (width > height) {
      height = Math.round((height * maxWidth) / width)
      width = maxWidth
    }
    else {
      width = Math.round((width * maxHeight) / height)
      height = maxHeight
    }

    return { width, height }
  }

  /**
   * Process image using canvas
   * @param {HTMLImageElement} img - Image element to process
   * @param {File} originalFile - Original file for reference
   * @returns {{ dataUrl: string, format: string }} - Processed image data URL and format
   */
  processWithCanvas = (img, originalFile) => {
    const { width, height } = this.calculateDimensions(img.width, img.height)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    // Use better quality settings for TIFF conversion
    if (originalFile.type.includes('tiff') || originalFile.type.includes('tif')) {
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
    }
    ctx.drawImage(img, 0, 0, width, height)

    const outputFormat = this.determineOutputFormat(originalFile.type, originalFile.size)
    const quality = originalFile.size > this.config.maxFileSize
      ? this.config.compressedQuality
      : this.config.defaultQuality

    return {
      dataUrl: canvas.toDataURL(outputFormat, quality),
      format: outputFormat,
    }
  }

  /**
   * Process an image file
   * @param {File} file - The image file to process
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<{ dataUrl: string, width: number, height: number, format: string }>}
   */
  async processImage(file, onProgress = null) {
    const { supported, needsConversion } = this.checkFileSupport(file.type)

    if (!supported) {
      throw new Error(`Unsupported file type: ${file.type}`)
    }

    try {
      if (onProgress) onProgress(0.3)

      if (needsConversion) {
        // Handle TIFF files through Rails
        const converted = await this.convertTiffWithRails(file)

        if (onProgress) onProgress(1)
        return {
          dataUrl: converted.dataUrl,
          width: converted.originalWidth,
          height: converted.originalHeight,
          format: 'image/jpeg',
          convertedFromTiff: true,
        }
      }
      else {
        // Handle native browser-supported formats as before
        const dataUrl = await this.fileToDataUrl(file)

        if (onProgress) onProgress(0.6)
        const img = await this.dataUrlToImage(dataUrl)
        const processed = this.processWithCanvas(img, file)

        if (onProgress) onProgress(1)
        return {
          dataUrl: processed.dataUrl,
          width: img.width,
          height: img.height,
          format: processed.format,
        }
      }
    }
    catch (error) {
      throw new Error(`Failed to process image: ${error.message}`)
    }
  }
}
